/*
  <kg-nutrition>: laedt die Naehrwert-CSV der Produktfamilie (oder des Produkts),
  liest sie schluesselbasiert und baut die Tabellen:
  Naehrwerte, Vitamine, Mineralstoffe, Aminosaeuren, aktive Inhaltsstoffe,
  dazu Zutaten mit hervorgehobenen Allergenen, Hinweise, Warnhinweise und
  Verkehrsbezeichnung. Der Geschmack folgt dem Hauptdropdown der Produktseite
  (variant:change) und laesst sich im eigenen Dropdown wechseln. Ein Umschalter
  zeigt wahlweise die Werte je 100 g/ml bzw. je Stueck oder je Portion bzw.
  Tagesdosis (eine Wertespalte, damit nichts umbricht).
  Texte je Sprache: Spalte mit Kuerzel (ingredients_en), Rueckfall Englisch,
  dann die Spalte ohne Kuerzel (Deutsch). Im Drawer oder Aufklapper wird die
  Datei erst beim ersten Oeffnen geladen.
*/

/* Schluesselliste wird mit versionierter URL (asset_url, data-keys-url) geladen, ein relativer Import bliebe im Browser-Cache haengen */
let KEYS = {};
let META_KEYS = [];
let TEXT_KEYS = [];
let FORMS = [];
let keysLoaded = null;

async function loadKeys(url) {
  if (!keysLoaded) {
    keysLoaded = import(url).then((module) => {
      KEYS = module.KEYS;
      META_KEYS = module.META_KEYS;
      TEXT_KEYS = module.TEXT_KEYS;
      FORMS = module.FORMS;
    });
  }
  return keysLoaded;
}

const normalize = (value) => (value || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');

function parseNumber(raw) {
  if (raw === undefined || raw === null) return null;
  let text = raw.toString().trim().replace(/\s/g, '');
  if (!text || text === '-') return null;
  /* Deutsche Schreibweise: Komma als Dezimaltrenner, Punkt nur als Tausenderpunkt erlaubt (13.500) */
  if (text.includes(',')) text = text.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(text)) text = text.replace(/\./g, '');
  const value = Number(text.replace(/[^\d.-]/g, ''));
  return Number.isFinite(value) ? value : null;
}

function decodeBytes(buffer) {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder('windows-1252').decode(buffer);
  }
}

/* Einfacher CSV-Parser mit Anfuehrungszeichen, Trennzeichen aus der Kopfzeile erkannt */
function parseCsv(text) {
  const clean = text.replace(/^﻿/, '');
  const firstLine = clean.split(/\r?\n/)[0] || '';
  const delimiter = [';', ',', '\t'].sort((a, b) => firstLine.split(b).length - firstLine.split(a).length)[0];
  const rows = [];
  let row = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < clean.length; i += 1) {
    const char = clean[i];
    if (quoted) {
      if (char === '"' && clean[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (char === '"') quoted = false;
      else cell += char;
    } else if (char === '"') quoted = true;
    else if (char === delimiter) {
      row.push(cell);
      cell = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && clean[i + 1] === '\n') i += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += char;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((cells) => cells.some((value) => value.trim() !== ''));
}

/* Kopfzeile: "vitamin_c (mg)" -> vitamin_c; "active:Koffein (mg)" -> freier Aktivstoff; "ingredients_en" -> Text mit Sprache */
function parseHeader(raw) {
  const text = raw.trim();
  const active = /^active\s*:\s*(.+)$/i.exec(text);
  if (active) {
    const unit = /\(([^)]+)\)\s*$/.exec(active[1]);
    return { type: 'active', label: active[1].replace(/\(([^)]+)\)\s*$/, '').trim(), unit: unit ? unit[1].trim() : '' };
  }
  const key = text.replace(/\(([^)]+)\)\s*$/, '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (KEYS[key]) return { type: 'value', key };
  if (META_KEYS.includes(key)) return { type: 'meta', key };
  const lang = /^([a-z_]+?)_([a-z]{2})$/.exec(key);
  if (TEXT_KEYS.includes(key)) return { type: 'text', key, lang: '' };
  if (lang && TEXT_KEYS.includes(lang[1])) return { type: 'text', key: lang[1], lang: lang[2] };
  return { type: 'unknown', key: text };
}

class KgNutrition extends HTMLElement {
  connectedCallback() {
    this.labels = JSON.parse(this.dataset.labels || '{}');
    this.locale = (this.dataset.locale || 'de').slice(0, 2).toLowerCase();
    this.flavorIndex = this.dataset.flavorIndex === '' || this.dataset.flavorIndex === undefined ? -1 : Number(this.dataset.flavorIndex);
    this.flavor = this.dataset.flavor || '';
    this.basis = null;
    this.content = this.querySelector('[data-nutrition-content]');
    this.basisSlot = this.querySelector('[data-nutrition-basis]');
    this.select = this.querySelector('[data-nutrition-flavor]');
    this.select?.addEventListener('change', () => {
      this.flavor = this.schluessel(this.select.value);
      this.render();
    });
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-basis]');
      if (!button) return;
      this.basis = button.dataset.basis;
      this.render();
    });
    this.onVariant = (event) => {
      const options = event.detail?.variant?.options;
      if (!options || this.flavorIndex < 0) return;
      const gewaehlt = options[this.flavorIndex] || '';
      this.flavor = this.schluessel(gewaehlt) || this.flavor;
      if (this.select && [...this.select.options].some((option) => option.value === gewaehlt)) {
        this.select.value = gewaehlt;
        /* Custom-Dropdown (kg-select) den neuen Wert anzeigen lassen */
        this.select.closest('kg-select')?.sync?.();
      }
      this.render();
    };
    document.addEventListener('variant:change', this.onVariant);
    /* Im Drawer oder Aufklapper erst beim ersten Oeffnen laden */
    const dialog = this.closest('kg-dialog');
    const details = this.closest('details');
    if (dialog && !dialog.querySelector('dialog')?.open) dialog.addEventListener('dialog:open', () => this.load(), { once: true });
    else if (details && !details.open) details.addEventListener('toggle', () => details.open && this.load(), { once: true });
    else this.load();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.onVariant);
  }

  t(key, values = {}) {
    let text = this.labels[key] || key;
    Object.entries(values).forEach(([name, value]) => {
      text = text.replace(`[${name}]`, value);
    });
    return text;
  }

  async load() {
    if (!this.dataset.src || this.entries) return;
    try {
      await loadKeys(this.dataset.keysUrl || new URL('./nutrition-keys.js', import.meta.url).href);
      const response = await fetch(this.dataset.src);
      if (!response.ok) throw new Error(`CSV ${response.status}`);
      const rows = parseCsv(decodeBytes(await response.arrayBuffer()));
      this.parse(rows);
      this.render();
    } catch {
      this.showMessage(this.t('no_data'));
    }
  }

  parse(rows) {
    const headers = rows[0].map(parseHeader);
    this.unknown = headers.filter((header) => header.type === 'unknown').map((header) => header.key);
    this.entries = rows.slice(1).map((cells) => {
      const entry = { meta: {}, texts: {}, values: {}, actives: [] };
      headers.forEach((header, index) => {
        const raw = cells[index] === undefined ? '' : cells[index].toString().trim();
        if (header.type === 'meta') entry.meta[header.key] = raw;
        else if (header.type === 'text') {
          entry.texts[header.key] = entry.texts[header.key] || {};
          entry.texts[header.key][header.lang || 'base'] = raw;
        } else if (header.type === 'value') {
          const value = parseNumber(raw);
          if (value !== null) entry.values[header.key] = value;
        } else if (header.type === 'active') {
          const value = parseNumber(raw);
          if (value !== null) entry.actives.push({ label: header.label, unit: header.unit, value });
        }
      });
      entry.flavor = entry.meta.flavor || '';
      return entry;
    });
  }

  /*
    Der Sortenname, unter dem die CSV ihre Zeilen fuehrt. Das ist nicht der angezeigte Wert:
    Shopify uebersetzt Optionswerte, die Datei bleibt deutsch beschriftet. Liquid gibt den
    deutschen Namen je Eintrag als data-key mit (siehe snippets/option-basis). Fehlt er,
    bleibt der angezeigte Wert — dann greift wie vorher die Teiltreffer-Suche.
  */
  schluessel(wert) {
    if (!wert) return wert;
    const eintrag = this.select && [...this.select.options].find((option) => option.value === wert);
    return (eintrag && eintrag.dataset.key) || wert;
  }

  current() {
    if (!this.entries?.length) return null;
    const wanted = normalize(this.flavor);
    return (
      this.entries.find((entry) => normalize(entry.flavor) === wanted) ||
      this.entries.find((entry) => wanted && normalize(entry.flavor) && (wanted.includes(normalize(entry.flavor)) || normalize(entry.flavor).includes(wanted))) ||
      this.entries.find((entry) => !entry.flavor) ||
      (this.flavorIndex < 0 ? this.entries[0] : null)
    );
  }

  text(entry, key) {
    const texts = entry.texts[key];
    if (!texts) return '';
    return texts[this.locale] || texts.en || texts.base || '';
  }

  format(value, integer = false) {
    if (value === null || value === undefined) return '';
    const digits = integer || Math.abs(value) >= 100 ? 0 : Math.abs(value) < 1 ? 2 : 1;
    return new Intl.NumberFormat(this.locale, { minimumFractionDigits: 0, maximumFractionDigits: digits }).format(value);
  }

  /*
    Moegliche Bezuege je Darreichungsform: Pulver, Riegel und Fluessiges "je 100 g/ml" und "je Portion",
    Kapseln und Tabletten "je Stueck" und "je Tagesdosis". Die CSV liefert Werte je 100 g
    (values_basis 100, Standard bei Pulver) oder je Portion/Tagesdosis (values_basis portion,
    Standard bei Kapseln und Tabletten); der jeweils andere Bezug wird gerechnet.
  */
  layout(entry) {
    const form = FORMS.includes(entry.meta.form) ? entry.meta.form : 'powder';
    const portion = parseNumber(entry.meta.portion_size) || 0;
    const pieces = form === 'capsule' || form === 'tablet';
    const basis = entry.meta.values_basis || (pieces ? 'portion' : '100');
    const unitLabel = form === 'liquid' ? 'ml' : 'g';
    const formName = (count) => this.t(count === 1 ? `form_${form}_one` : `form_${form}_other`);
    const columns = [];
    if (pieces) {
      columns.push({ id: 'base', label: this.t('per_piece', { unit: formName(1) }), factor: basis === 'portion' && portion ? 1 / portion : 1 });
      if (portion) columns.push({ id: 'portion', label: this.t('per_daily', { count: this.format(portion, true), unit: formName(portion) }), factor: basis === 'portion' ? 1 : portion });
    } else {
      columns.push({ id: 'base', label: this.t('per_100', { unit: unitLabel }), factor: basis === 'portion' && portion ? 100 / portion : 1 });
      if (portion) columns.push({ id: 'portion', label: this.t('per_portion', { size: `${this.format(portion)}${unitLabel}` }), factor: basis === 'portion' ? 1 : portion / 100 });
    }
    /* Gewaehlter Bezug: Nutzerwahl, sonst Tagesdosis bei Stueckware, sonst 100 g */
    const wanted = this.basis || (pieces ? 'portion' : 'base');
    const active = columns.find((column) => column.id === wanted) || columns[0];
    return { columns, active, pieces, portion };
  }

  render() {
    if (!this.content) return;
    const entry = this.current();
    if (!entry) {
      this.showMessage(this.t('no_data'));
      return;
    }
    const layout = this.layout(entry);
    this.renderBasis(layout);
    const view = { columns: [layout.active], active: layout.active };
    const parts = [];
    const nutritionKeys = Object.keys(KEYS).filter((key) => KEYS[key].group === 'nutrition' && entry.values[key] !== undefined);
    if (nutritionKeys.length) parts.push(this.table('nutrition', this.nutritionRows(entry, view), view, 'ri'));
    ['vitamins', 'minerals'].forEach((group) => {
      const keys = Object.keys(KEYS).filter((key) => KEYS[key].group === group && entry.values[key] !== undefined);
      if (keys.length) parts.push(this.table(group, keys.map((key) => this.row(key, entry.values[key], view)), view, 'nrv'));
    });
    const aminoKeys = Object.keys(KEYS).filter((key) => KEYS[key].group === 'amino' && entry.values[key] !== undefined);
    if (aminoKeys.length) parts.push(this.aminoTable(entry, aminoKeys, layout));
    if (entry.actives.length) {
      parts.push(
        this.table(
          'active',
          entry.actives.map((active) => ({ label: active.label, unit: active.unit, value: active.value, sub: /^(davon|of which|dont|waarvan)\b/i.test(active.label) })),
          view,
          null,
        ),
      );
    }
    parts.push(this.texts(entry));
    if (this.hasAttribute('data-debug') && this.unknown?.length) {
      parts.push(`<p class="nutrition__debug">${this.escape(this.t('unknown_columns', { columns: this.unknown.join(', ') }))}</p>`);
    }
    this.content.innerHTML = parts.join('');
  }

  /* Umschalter zwischen den Bezuegen, nur wenn es zwei gibt */
  renderBasis(layout) {
    if (!this.basisSlot) return;
    if (layout.columns.length < 2) {
      this.basisSlot.hidden = true;
      this.basisSlot.innerHTML = '';
      return;
    }
    this.basisSlot.hidden = false;
    this.basisSlot.innerHTML = layout.columns
      .map(
        (column) =>
          `<button type="button" class="nutrition__basis-button${column.id === layout.active.id ? ' is-active' : ''}" data-basis="${column.id}" aria-pressed="${column.id === layout.active.id}">${this.escape(column.label)}</button>`,
      )
      .join('');
  }

  nutritionRows(entry, view) {
    const rows = [];
    const order = Object.keys(KEYS).filter((key) => KEYS[key].group === 'nutrition');
    order.forEach((key) => {
      if (entry.values[key] === undefined) return;
      if (key === 'energy_kj') return;
      if (key === 'energy_kcal') {
        const kj = entry.values.energy_kj;
        rows.push({
          label: this.t('key_energy'),
          cells: view.columns.map((column) => {
            const kcal = this.format(entry.values.energy_kcal * column.factor, true);
            return kj === undefined ? `${kcal} kcal` : `${this.format(kj * column.factor, true)} kJ / ${kcal} kcal`;
          }),
          percent: Math.round((entry.values.energy_kcal * view.active.factor * 100) / KEYS.energy_kcal.ri),
        });
        return;
      }
      rows.push(this.row(key, entry.values[key], view));
    });
    return rows;
  }

  row(key, value, view) {
    const info = KEYS[key];
    return {
      label: this.t(`key_${key}`),
      sub: Boolean(info.sub),
      cells: view.columns.map((column) => `${this.format(value * column.factor, info.integer)} ${info.unit}`),
      percent: info.nrv || info.ri ? Math.round((value * view.active.factor * 100) / (info.nrv || info.ri)) : null,
    };
  }

  /* Aminosaeuren: je 100 g Produkt (Standard) oder je 100 g Protein (amino_basis protein, Portion ueber den Proteingehalt) */
  aminoTable(entry, keys, layout) {
    const perProtein = (entry.meta.amino_basis || '').toLowerCase() === 'protein';
    let column = layout.active;
    if (perProtein) {
      const protein = entry.values.protein;
      const portion = layout.columns.find((item) => item.id === 'portion');
      if (layout.active.id === 'portion' && protein !== undefined && portion) column = { id: 'portion', label: portion.label, factor: (protein * portion.factor) / 100 };
      else column = { id: 'protein', label: this.t('per_100_protein'), factor: 1 };
    }
    const view = { columns: [column], active: column };
    const rows = keys.map((key) => ({
      label: this.t(`key_${key}`),
      cells: [`${this.format(entry.values[key] * column.factor)} ${KEYS[key].unit}`],
    }));
    return this.table('amino', rows, view, null);
  }

  /*
    Prozentspalte: "ri" = Referenzmenge fuer Erwachsene bei den Grundnaehrwerten, "nrv" = Naehrstoffbezugswert
    bei Vitaminen und Mineralstoffen, null = keine. Fussnoten je Tabelle darunter; unter den Grundnaehrwerten
    zusaetzlich der Hinweis auf natuerliche Schwankungen und der Zusatzhinweis aus dem Block.
  */
  table(group, rows, view, percentColumn) {
    const showNrv = Boolean(percentColumn) && rows.some((row) => row.percent !== null && row.percent !== undefined);
    const head = view.columns.map((column) => `<th scope="col">${this.escape(column.label)}</th>`).join('');
    const body = rows
      .map((row) => {
        const cells = row.cells
          ? row.cells.map((cell) => `<td>${this.escape(cell)}</td>`).join('')
          : view.columns.map((column) => `<td>${this.escape(`${this.format(row.value * column.factor)} ${row.unit}`.trim())}</td>`).join('');
        const nrv = showNrv ? `<td>${row.percent !== null && row.percent !== undefined ? `${this.format(row.percent, true)} %` : '–'}</td>` : '';
        return `<tr${row.sub ? ' class="nutrition__row--sub"' : ''}><th scope="row">${this.escape(row.label)}</th>${cells}${nrv}</tr>`;
      })
      .join('');
    const notes = [];
    if (group === 'nutrition') notes.push(this.escape(this.t('variation_note')));
    if (showNrv) notes.push(this.escape(this.t(`${percentColumn}_note`)));
    if (group === 'nutrition' && this.dataset.extraNote) notes.push(this.dataset.extraNote);
    const title = group === 'nutrition' ? `${this.t('group_nutrition')}*` : this.t(`group_${group}`);
    /* Tabellentitel steht in der ersten Kopfzelle oben links */
    return `
      <div class="nutrition__group nutrition__group--${group}">
        <div class="nutrition__scroll">
          <table class="nutrition__table">
            <thead><tr><th scope="col" class="nutrition__title">${this.escape(title)}</th>${head}${showNrv ? `<th scope="col">${this.escape(this.t(percentColumn))}</th>` : ''}</tr></thead>
            <tbody>${body}</tbody>
          </table>
        </div>
        ${notes.map((note) => `<div class="nutrition__note">${note}</div>`).join('')}
      </div>`;
  }

  texts(entry) {
    const blocks = [];
    const ingredients = this.text(entry, 'ingredients');
    if (ingredients) {
      const allergens = this.text(entry, 'allergens')
        .split(/[,;.]/)
        .map((item) => item.trim())
        .filter(Boolean);
      let html = this.escape(ingredients);
      allergens.forEach((allergen) => {
        const pattern = new RegExp(`(^|[^\\p{L}])(${allergen.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})(?=$|[^\\p{L}])`, 'giu');
        html = html.replace(pattern, '$1<strong>$2</strong>');
      });
      blocks.push({ key: 'ingredients', html });
    }
    ['consumption', 'sales_description', 'notes', 'warnings'].forEach((key) => {
      const value = this.text(entry, key);
      if (value) blocks.push({ key, html: this.escape(value).replace(/\n/g, '<br>') });
    });
    if (!blocks.length) return '';
    const html = blocks
      .map((block) => `<div class="nutrition__text nutrition__text--${block.key}"><h3 class="nutrition__subheading">${this.escape(this.t(`text_${block.key}`))}</h3><p>${block.html}</p></div>`)
      .join('');
    return `<div class="nutrition__texts">${html}</div>`;
  }

  showMessage(text) {
    if (this.content) this.content.innerHTML = `<p class="nutrition__empty">${this.escape(text)}</p>`;
  }

  escape(value) {
    return (value || '').toString().replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
  }
}

customElements.define('kg-nutrition', KgNutrition);
